import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDemoRatio } from '@/contexts/DemoContext';

// Custom hook to safely use demo context
const useSafeDemoRatio = () => {
  try {
    return useDemoRatio();
  } catch (error) {
    // Demo context not available, return default values
    return {
      portraitRatio: 'freestyle' as const,
      setPortraitRatio: () => { },
      isDemoMode: false
    };
  }
};

const SupportFooter: React.FC = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);
  const isDraggingRef = useRef(false);
  const positionRef = useRef(position);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  // Demo context integration (safe)
  const { portraitRatio } = useSafeDemoRatio();

  // Hide support button on FAQ and support pages
  const shouldHideButton = pathname === '/faq' || pathname === '/support';

  const BUTTON_SIZE = 48; // Reduced by 20% from 60 to 48
  const MARGIN = 12; // Reduced margin for tighter spacing

  // Use full window so the button can be dragged anywhere including over the header
  const getEffectiveViewport = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
    offsetX: 0,
    offsetY: 0
  });

  // Set default position to top-right
  const setDefaultPosition = () => {
    const viewport = getEffectiveViewport();
    const defaultX = viewport.width - BUTTON_SIZE - MARGIN + viewport.offsetX;
    const defaultY = MARGIN + viewport.offsetY;
    setPosition({ x: defaultX, y: defaultY });
  };

  // Constrain position to viewport bounds
  const constrainPosition = (pos: { x: number; y: number }) => {
    const viewport = getEffectiveViewport();
    const maxX = viewport.width - BUTTON_SIZE + viewport.offsetX;
    const maxY = viewport.height - BUTTON_SIZE + viewport.offsetY;
    const minX = viewport.offsetX;
    const minY = viewport.offsetY;

    return {
      x: Math.max(minX, Math.min(pos.x, maxX)),
      y: Math.max(minY, Math.min(pos.y, maxY))
    };
  };

  // Save position to localStorage
  const savePosition = (newPosition: { x: number; y: number }) => {
    localStorage.setItem('supportFooterPosition', JSON.stringify(newPosition));
  };

  // Load saved position from localStorage or set default
  useEffect(() => {
    if (isInitialized.current) return;

    const savedPosition = localStorage.getItem('supportFooterPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        const constrained = constrainPosition(parsed);
        setPosition(constrained);
      } catch (error) {
        console.warn('Failed to parse saved support footer position');
        setDefaultPosition();
      }
    } else {
      setDefaultPosition();
    }

    isInitialized.current = true;
  }, []);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  // Handle demo ratio changes
  useEffect(() => {
    // When demo ratio changes, adjust button position to stay within new viewport
    // Use a delay to ensure the DOM has updated with new dimensions after CSS transition
    const timeoutId = setTimeout(() => {
      setPosition(prev => {
        const constrained = constrainPosition(prev);
        if (constrained.x !== prev.x || constrained.y !== prev.y) {
          console.log('Demo ratio changed, adjusting support footer position:', {
            ratio: portraitRatio,
            old: prev,
            new: constrained,
            viewport: getEffectiveViewport()
          });
          savePosition(constrained);
          return constrained;
        }
        return prev;
      });
    }, 350); // Increased to 350ms to account for 300ms CSS transition

    return () => clearTimeout(timeoutId);
  }, [portraitRatio]);

  // Handle window resize and orientation changes to keep button in viewport
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      // Debounce resize events
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const constrained = constrainPosition(position);
        if (constrained.x !== position.x || constrained.y !== position.y) {
          console.log('Screen ratio changed, adjusting support footer position:', {
            old: position,
            new: constrained,
            viewport: getEffectiveViewport()
          });
          setPosition(constrained);
          savePosition(constrained);
        }
      }, 100);
    };

    const handleOrientationChange = () => {
      // Handle orientation changes with a longer delay
      setTimeout(() => {
        const constrained = constrainPosition(position);
        console.log('Orientation changed, adjusting support footer position:', {
          old: position,
          new: constrained,
          viewport: getEffectiveViewport()
        });
        setPosition(constrained);
        savePosition(constrained);
      }, 500);
    };

    // Add media query listener for screen ratio changes (demo mode)
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMediaChange = () => {
      setTimeout(() => {
        const constrained = constrainPosition(position);
        console.log('Media query changed, adjusting support footer position:', {
          old: position,
          new: constrained,
          viewport: getEffectiveViewport()
        });
        setPosition(constrained);
        savePosition(constrained);
      }, 200);
    };

    // Add a MutationObserver to detect demo container changes
    const observer = new MutationObserver(() => {
      setTimeout(() => {
        const constrained = constrainPosition(position);
        if (constrained.x !== position.x || constrained.y !== position.y) {
          console.log('Demo container changed, adjusting support footer position:', {
            old: position,
            new: constrained,
            viewport: getEffectiveViewport()
          });
          setPosition(constrained);
          savePosition(constrained);
        }
      }, 100);
    });

    // Observe changes to the demo container
    const demoContainer = document.querySelector('[class*="demo-ratio"]') || document.body;
    observer.observe(demoContainer, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: false,
      subtree: false
    });

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setHasMoved(false);
    setIsDragging(false);
    isDraggingRef.current = false;

    const startX = e.clientX;
    const startY = e.clientY;
    let hasActuallyMoved = false;
    const dragStartOffset = {
      x: startX - positionRef.current.x,
      y: startY - positionRef.current.y
    };
    setDragStart(dragStartOffset);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const moveDistance = Math.sqrt(
        Math.pow(moveEvent.clientX - startX, 2) + Math.pow(moveEvent.clientY - startY, 2)
      );

      if (moveDistance > 5) {
        hasActuallyMoved = true;
        setHasMoved(true);
        setIsDragging(true);
        isDraggingRef.current = true;

        const newX = moveEvent.clientX - dragStartOffset.x;
        const newY = moveEvent.clientY - dragStartOffset.y;
        const constrained = constrainPosition({ x: newX, y: newY });
        setPosition(constrained);
      }
    };

    const handleMouseUp = () => {
      if (!hasActuallyMoved && !isDraggingRef.current) {
        navigate('/support');
      }
      if (isDraggingRef.current) {
        savePosition(positionRef.current);
      }
      setIsDragging(false);
      isDraggingRef.current = false;
      setHasMoved(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setHasMoved(false);
    setIsDragging(false);
    isDraggingRef.current = false;

    const touch = e.touches[0];
    if (!touch) return;

    const startX = touch.clientX;
    const startY = touch.clientY;
    let hasActuallyMoved = false;
    let lastPosition = { ...positionRef.current };
    const dragStartOffset = {
      x: startX - positionRef.current.x,
      y: startY - positionRef.current.y
    };
    setDragStart(dragStartOffset);

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const t = moveEvent.touches[0] || moveEvent.changedTouches[0];
      if (!t) return;
      const moveDistance = Math.sqrt(
        Math.pow(t.clientX - startX, 2) + Math.pow(t.clientY - startY, 2)
      );
      if (moveDistance > 3) {
        hasActuallyMoved = true;
        setHasMoved(true);
        setIsDragging(true);
        isDraggingRef.current = true;
        const newX = t.clientX - dragStartOffset.x;
        const newY = t.clientY - dragStartOffset.y;
        const constrained = constrainPosition({ x: newX, y: newY });
        lastPosition = constrained;
        setPosition(constrained);
      }
    };

    const handleTouchEnd = (endEvent: TouchEvent) => {
      endEvent.preventDefault();
      if (!hasActuallyMoved && !isDraggingRef.current) {
        navigate('/support');
      }
      if (isDraggingRef.current) {
        savePosition(lastPosition);
      }
      setIsDragging(false);
      isDraggingRef.current = false;
      setHasMoved(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: false });
  };

  // Don't render the button on FAQ and support pages
  if (shouldHideButton) {
    return null;
  }

  return (
    <div
      ref={buttonRef}
      className="fixed z-40"
      style={{
        left: position.x,
        top: position.y,
        transform: isDragging ? 'scale(1.05)' : 'scale(1)',
        transition: isDragging ? 'none' : 'transform 0.1s ease',
        pointerEvents: 'auto',
        touchAction: 'none',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none'
      }}
    >
      <div
        className="bg-blue-600 text-white rounded-full shadow-lg cursor-move w-12 h-12 flex items-center justify-center select-none touch-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        title="Support - Drag to move • Click to open support"
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <MessageCircle className="w-6 h-6 pointer-events-none" />
      </div>
    </div>
  );
};

export default SupportFooter;
